/**
 * Account billing (`/settings/billing`, `/de/einstellungen/abrechnung`, ...):
 * current plan, renewal / cancel notice, "Manage billing" (Stripe Customer
 * Portal), the Team seats panel, the early-access state while billing is not
 * live, and the post-checkout success state (`?checkout=success`) that shows
 * an optimistic "activating" until the webhook-written entitlement flips.
 */
import { useAppAuth } from '../lib/auth/app-auth'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { ConvexError } from 'convex/values'
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Sparkles,
  Trash2,
  Users,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'
import { isValidEmail } from '../lib/email'
import { formatDate, localizedPath, useTranslation } from '../lib/i18n'
import type { TranslationKey } from '../lib/i18n/dictionary'
import { useBillingActions } from '../lib/use-billing'
import type { BillingErrorCode } from '../lib/use-billing'
import { PageIntro } from './not-found'
import { SettingsNav } from './settings-nav'

/** After this long without the webhook, say so honestly. */
const ACTIVATION_PATIENCE_MS = 45_000

export function BillingSettingsPage() {
  const { t } = useTranslation()
  return (
    <main className="page-shell">
      <PageIntro
        eyebrow={t('pages.settings.eyebrow')}
        title={t('billing.title')}
        text={t('billing.text')}
      />
      <div className="settings-layout">
        <SettingsNav current="settingsBilling" />
        <section className="editorial-block billing-panel">
          <BillingContent />
        </section>
      </div>
    </main>
  )
}

type CheckoutReturn = 'none' | 'activating' | 'slow' | 'activated'

function BillingContent() {
  const { locale, t } = useTranslation()
  const { isLoaded, isSignedIn } = useAppAuth()
  const { isAuthenticated } = useConvexAuth()
  const billing = useQuery(api.billing.myBilling, isAuthenticated ? {} : 'skip')
  const team = useQuery(api.teamSeats.myTeam, isAuthenticated ? {} : 'skip')
  const actions = useBillingActions(locale)
  const [checkoutReturn, setCheckoutReturn] = useState<CheckoutReturn>('none')

  // `?checkout=success` from Stripe: optimistic "activating" state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') !== 'success') return
    setCheckoutReturn('activating')
    const timer = window.setTimeout(
      () =>
        setCheckoutReturn((current) =>
          current === 'activating' ? 'slow' : current,
        ),
      ACTIVATION_PATIENCE_MS,
    )
    return () => window.clearTimeout(timer)
  }, [])

  const isPro =
    billing?.signedIn === true ? billing.entitlement.isPro : false
  useEffect(() => {
    if (isPro && (checkoutReturn === 'activating' || checkoutReturn === 'slow')) {
      setCheckoutReturn('activated')
      // Drop the Stripe params so a reload does not replay the banner.
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [isPro, checkoutReturn])

  if (isLoaded && !isSignedIn) {
    return (
      <div className="billing-empty">
        <h2>{t('billing.title')}</h2>
        <p>{t('billing.signedOut')}</p>
        <a
          className="primary-button"
          href={`${localizedPath('login', locale)}?redirect_url=${encodeURIComponent(localizedPath('settingsBilling', locale))}`}
        >
          {t('billing.signIn')}
        </a>
      </div>
    )
  }

  if (billing === undefined || billing.signedIn !== true) {
    return (
      <div
        className="billing-skeleton pro-skeleton-section"
        role="status"
        aria-live="polite"
      >
        <span className="sr-only">{t('billing.loading')}</span>
        <span className="pro-skeleton-heading" aria-hidden="true" />
        <span className="pro-skeleton-line" aria-hidden="true" />
        <span className="pro-skeleton-line is-short" aria-hidden="true" />
      </div>
    )
  }

  const { entitlement, subscription, live } = billing
  const planName =
    entitlement.plan === 'team'
      ? t('pricing.plans.team.name')
      : entitlement.plan === 'pro'
        ? t('pricing.plans.pro.name')
        : t('pricing.plans.free.name')
  const date = (value: number) => formatDate(value, locale)

  return (
    <div className="billing-stack">
      <CheckoutReturnBanner state={checkoutReturn} />

      <article className="billing-plan-card">
        <header>
          <span className="eyebrow">{t('billing.currentPlan')}</span>
          <div className="billing-plan-title">
            <h2>{planName}</h2>
            {subscription ? (
              <span className={`billing-status is-${subscription.status}`}>
                {statusLabel(subscription.status, t)}
              </span>
            ) : null}
          </div>
        </header>

        {subscription && entitlement.source === 'subscription' ? (
          <dl className="billing-facts">
            {subscription.interval ? (
              <div>
                <dt>{t('billing.billingLabel')}</dt>
                <dd>
                  {subscription.interval === 'year'
                    ? t('billing.intervalYear')
                    : t('billing.intervalMonth')}
                </dd>
              </div>
            ) : null}
            {subscription.plan === 'team' ? (
              <div>
                <dt>{t('billing.seatsLabel')}</dt>
                <dd>{t('billing.seatsValue', { count: subscription.seats })}</dd>
              </div>
            ) : null}
            <div>
              <dt>
                {subscription.cancelAtPeriodEnd
                  ? t('billing.endsLabel')
                  : t('billing.renewsLabel')}
              </dt>
              <dd>{date(subscription.currentPeriodEnd)}</dd>
            </div>
          </dl>
        ) : null}

        {subscription?.cancelAtPeriodEnd && entitlement.isPro ? (
          <p className="billing-notice">
            {t('billing.cancelNotice', {
              date: date(subscription.currentPeriodEnd),
            })}
          </p>
        ) : null}
        {subscription?.status === 'past_due' ? (
          <p className="billing-notice is-warning">
            <AlertTriangle size={16} aria-hidden="true" />
            {t('billing.pastDueNotice')}
          </p>
        ) : null}

        {entitlement.source === 'team_seat' ? (
          <p className="billing-note">{t('billing.sourceSeat')}</p>
        ) : entitlement.source === 'grant' ? (
          <p className="billing-note">
            {t('billing.sourceGrant', {
              date: entitlement.currentPeriodEnd
                ? date(entitlement.currentPeriodEnd)
                : '',
            })}
          </p>
        ) : entitlement.source === 'admin' ? (
          <p className="billing-note">{t('billing.sourceAdmin')}</p>
        ) : !entitlement.isPro ? (
          <p className="billing-note">{t('billing.freeText')}</p>
        ) : null}

        <div className="billing-actions">
          {billing.hasBillingCustomer ? (
            <button
              className="primary-button"
              type="button"
              onClick={() => void actions.openPortal()}
              disabled={actions.pending !== null}
              aria-busy={actions.pending !== null || undefined}
            >
              {actions.pending ? (
                <Loader2 size={17} className="spin" aria-hidden="true" />
              ) : null}
              {actions.pending ? t('billing.opening') : t('billing.manage')}
            </button>
          ) : null}
          {!entitlement.isPro || entitlement.source === 'grant' ? (
            <a
              className={
                billing.hasBillingCustomer ? 'ghost-button' : 'primary-button'
              }
              href={
                live
                  ? localizedPath('pricing', locale)
                  : `${localizedPath('pricing', locale)}#founding`
              }
            >
              {live ? t('billing.seePlans') : t('pro.earlyAccessCta')}
              <ArrowRight size={16} aria-hidden="true" />
            </a>
          ) : null}
        </div>
        {billing.hasBillingCustomer ? (
          <small className="billing-hint">{t('billing.manageHint')}</small>
        ) : null}
        {actions.error ? (
          <p className="form-status is-error" role="alert">
            {portalError(actions.error, t)}
          </p>
        ) : null}
      </article>

      {!live && entitlement.source !== 'subscription' ? (
        <aside className="billing-early">
          <Sparkles size={18} aria-hidden="true" />
          <div>
            <strong>{t('billing.earlyTitle')}</strong>
            <p>{t('billing.earlyText')}</p>
          </div>
        </aside>
      ) : null}

      {team ? <TeamSeatsPanel team={team} /> : null}
    </div>
  )
}

function CheckoutReturnBanner({ state }: { state: CheckoutReturn }) {
  const { locale, t } = useTranslation()
  if (state === 'none') return null
  if (state === 'activated') {
    return (
      <div className="checkout-return is-done" role="status">
        <CheckCircle2 size={22} aria-hidden="true" />
        <div>
          <strong>{t('billing.activatedTitle')}</strong>
          <p>{t('billing.activatedText')}</p>
        </div>
        <a
          className="primary-button"
          href={localizedPath('course', locale, {
            courseSlug: 'modern-app-stack',
          })}
        >
          {t('billing.activatedCta')}
          <ArrowRight size={16} aria-hidden="true" />
        </a>
      </div>
    )
  }
  return (
    <div className="checkout-return" role="status" aria-live="polite">
      <Loader2 size={22} className="spin" aria-hidden="true" />
      <div>
        <strong>{t('billing.activatingTitle')}</strong>
        <p>
          {state === 'slow'
            ? t('billing.slowText')
            : t('billing.activatingText')}
        </p>
      </div>
    </div>
  )
}

function statusLabel(
  status: string,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const keys: Record<string, TranslationKey> = {
    active: 'billing.status.active',
    trialing: 'billing.status.trialing',
    past_due: 'billing.status.pastDue',
    canceled: 'billing.status.canceled',
    unpaid: 'billing.status.unpaid',
    incomplete: 'billing.status.incomplete',
    incomplete_expired: 'billing.status.canceled',
    paused: 'billing.status.paused',
  }
  return t(keys[status] ?? 'billing.status.active')
}

function portalError(
  code: BillingErrorCode,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (code === 'RATE_LIMITED') return t('pricing.errors.rateLimited')
  if (code === 'NO_CUSTOMER') return t('pricing.errors.noCustomer')
  return t('pricing.errors.generic')
}

/* ------------------------------- Team seats ------------------------------- */

type TeamView = {
  purchasedSeats: number
  assignableSeats: number
  members: Array<{
    id: Id<'teamSeats'>
    email: string
    createdAt: number
    active: boolean
  }>
}

const SEAT_ERROR_KEYS: Partial<Record<string, TranslationKey>> = {
  INVALID_EMAIL: 'billing.team.errors.invalidEmail',
  OWN_EMAIL: 'billing.team.errors.ownEmail',
  ALREADY_ASSIGNED: 'billing.team.errors.alreadyAssigned',
  SEATS_FULL: 'billing.team.errors.seatsFull',
  RATE_LIMITED: 'billing.team.errors.rateLimited',
}

function seatErrorKey(error: unknown): TranslationKey {
  if (error instanceof ConvexError) {
    const code = (error.data as { code?: string } | null)?.code
    if (code) return SEAT_ERROR_KEYS[code] ?? 'billing.team.errors.generic'
  }
  return 'billing.team.errors.generic'
}

export function TeamSeatsPanel({ team }: { team: TeamView }) {
  const { t } = useTranslation()
  const assign = useMutation(api.teamSeats.assignSeat)
  const remove = useMutation(api.teamSeats.removeSeat)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<TranslationKey | null>(null)
  const used = team.members.filter((member) => member.active).length
  const full = used >= team.assignableSeats

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const email = String(new FormData(form).get('email') ?? '')
    if (!isValidEmail(email)) {
      setError('billing.team.errors.invalidEmail')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await assign({ email })
      form.reset()
    } catch (caught) {
      setError(seatErrorKey(caught))
    } finally {
      setBusy(false)
    }
  }

  async function onRemove(seatId: Id<'teamSeats'>) {
    setError(null)
    try {
      await remove({ seatId })
    } catch (caught) {
      setError(seatErrorKey(caught))
    }
  }

  return (
    <section className="team-panel" aria-labelledby="team-panel-title">
      <header>
        <Users size={20} aria-hidden="true" />
        <div>
          <h2 id="team-panel-title">{t('billing.team.title')}</h2>
          <p>{t('billing.team.text')}</p>
        </div>
      </header>
      <div
        className="team-meter"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={team.assignableSeats}
        aria-valuenow={used}
        aria-label={t('billing.team.seatsUsed', {
          used,
          total: team.assignableSeats,
        })}
      >
        <span
          className="team-meter-fill"
          style={{
            width: `${team.assignableSeats ? (used / team.assignableSeats) * 100 : 100}%`,
          }}
        />
      </div>
      <p className="team-meter-label">
        {t('billing.team.seatsUsed', { used, total: team.assignableSeats })}
      </p>

      {team.members.length === 0 ? (
        <p className="team-empty">{t('billing.team.empty')}</p>
      ) : (
        <ul className="team-list">
          {team.members.map((member) => (
            <li key={member.id} className={member.active ? undefined : 'is-inactive'}>
              <span className="team-email">{member.email}</span>
              {member.active ? null : (
                <span className="team-inactive">{t('billing.team.inactive')}</span>
              )}
              <button
                type="button"
                className="team-remove"
                onClick={() => void onRemove(member.id)}
                aria-label={t('billing.team.removeLabel', { email: member.email })}
              >
                <Trash2 size={15} aria-hidden="true" />
                <span>{t('billing.team.remove')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <form className="team-form" onSubmit={onSubmit} noValidate>
        <label htmlFor="team-seat-email" className="sr-only">
          {t('billing.team.emailLabel')}
        </label>
        <input
          id="team-seat-email"
          name="email"
          type="email"
          autoComplete="off"
          placeholder={t('billing.team.emailPlaceholder')}
          disabled={full}
          required
        />
        <button className="primary-button" type="submit" disabled={busy || full}>
          {busy ? t('billing.team.assigning') : t('billing.team.assign')}
        </button>
      </form>
      {error ? (
        <p className="form-status is-error" role="alert">
          {t(error)}
        </p>
      ) : null}
      <small className="billing-hint">{t('billing.team.needMore')}</small>
    </section>
  )
}
